import { z } from "zod";

import { ErrorClassification } from "../../../sentry/classification";
import type { ErrorDetails } from "../domain";

const geographicAreaErrorTypeValues = [
  "GEOGRAPHIC_AREA_FETCH_ERROR",
  "GEOGRAPHIC_AREA_PARSING_ERROR",
  "CONTRACTOR_PROFILE_ID_NOT_FOUND",
  "DEFAULT_PRICING_MARKET_NOT_FOUND",
  "RATEBOOKS_NOT_RELINKED_ON_GEOGRAPHIC_AREA_CHANGE",
  "INVALID_GEOMETRY",
  "UNAUTHORIZED",
] as const;
const geographicAreaErrorTypeSchema = z.enum(geographicAreaErrorTypeValues);
const GeographicAreaErrorType = geographicAreaErrorTypeSchema.enum;
type GeographicAreaErrorType = z.infer<typeof geographicAreaErrorTypeSchema>;

export type GeographicAreaErrors =
  | { errorType: typeof GeographicAreaErrorType.GEOGRAPHIC_AREA_FETCH_ERROR; data: { type: string } }
  | { errorType: typeof GeographicAreaErrorType.GEOGRAPHIC_AREA_PARSING_ERROR; data: { id: string; type: string } }
  | {
      errorType: typeof GeographicAreaErrorType.CONTRACTOR_PROFILE_ID_NOT_FOUND;
      data: { serviceAreaId: string; serviceAreaName: string };
    }
  | {
      errorType: typeof GeographicAreaErrorType.DEFAULT_PRICING_MARKET_NOT_FOUND;
      data: { centroid: { lng: number; lat: number } };
    }
  | {
      errorType: typeof GeographicAreaErrorType.RATEBOOKS_NOT_RELINKED_ON_GEOGRAPHIC_AREA_CHANGE;
      data: {
        entityType: "ServiceArea" | "Market";
        entityId: string;
        entityName: string;
        previousGeographicAreaId: string;
        geographicAreaId: string;
      };
    }
  | {
      errorType: typeof GeographicAreaErrorType.INVALID_GEOMETRY;
      data: { geometryType: "Polygon" | "MultiPolygon"; polygonCount: number };
    }
  | {
      errorType: typeof GeographicAreaErrorType.UNAUTHORIZED;
      data: Record<string, never>;
    };

export const geographicAreaErrorDetails: Record<GeographicAreaErrorType, ErrorDetails> = {
  GEOGRAPHIC_AREA_FETCH_ERROR: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Geographic area fetch error",
  },
  GEOGRAPHIC_AREA_PARSING_ERROR: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Geographic area parsing error",
  },
  CONTRACTOR_PROFILE_ID_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Contractor profile id not found for service area",
  },
  DEFAULT_PRICING_MARKET_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No market found for service area centroid when using default pricing",
  },
  RATEBOOKS_NOT_RELINKED_ON_GEOGRAPHIC_AREA_CHANGE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Geographic area was re-minted with no ratebooks to carry over, leaving pricing unavailable",
  },
  INVALID_GEOMETRY: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Geographic area geometry is still invalid in MySQL after sanitizing and repair",
  },
  UNAUTHORIZED: {
    classification: ErrorClassification.AUTHORIZATION_ERROR,
    sentryDescription: "User not authorized to update service areas for contractor",
  },
};
